import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {
	FlexColumn,
	FlexRow,
	Icon,
	NestedCard,
	NumberField,
	SubHeading,
	Separator,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {formatHoursValue} from '../baselineTemplateFormat';
import {
	DELIVERABLE_DESCRIPTION_MAX_LENGTH,
	expenseEstimateCount,
	Option,
	PhaseDraft,
	phaseHours,
	timeEstimateCount,
	toNullableNumber,
} from './baselineTemplateDraft';
import {TimeEstimateLines} from './TimeEstimateLines';
import {ExpenseEstimateLines} from './ExpenseEstimateLines';

// NestedCard's content padding is tight for a form, so the body adds its own.
const Body = Styled(FlexColumn)`
	padding: 8px;
`;

type Props = {
	phase: PhaseDraft;
	index: number;
	includeDeliverables: boolean;
	canDelete: boolean;
	roles: Option[];
	categories: Option[];
	heldRoles?: Readonly<Record<string, Option>>;
	heldCategories?: Readonly<Record<string, Option>>;
	currency: string;
	fieldError: (path: string) => string | undefined;
	onChange: (patch: Partial<PhaseDraft>, changedPath?: string) => void;
	onDelete: () => void;
};

export const PhaseEditor = ({
	phase,
	index,
	includeDeliverables,
	canDelete,
	roles,
	categories,
	heldRoles,
	heldCategories,
	currency,
	fieldError,
	onChange,
	onDelete,
}: Props): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const cy = (part: string) => `baseline-template-phase-${index}-${part}`;

	return (
		<NestedCard colorTheme="dark" dataCy={cy('card')}>
			{/* The menu is always shown. Delete phase is disabled while this is the only phase. */}
			<NestedCard.Header
				mainIcon="phase"
				sectionName={formatMessage({id: 'baseline_templates.phase.number'}, {number: index + 1})}
				name={phase.name.trim() || undefined}
				showContextMenu
				contextMenuOptions={[
					{
						name: formatMessage({id: 'baseline_templates.phase.delete'}),
						onClick: onDelete,
						disabled: !canDelete,
					},
				]}
			>
				{phase.startWeek ? (
					<Text>{formatMessage({id: 'baseline_templates.phase.starts_week'}, {week: phase.startWeek})}</Text>
				) : null}
				{phase.durationDays ? (
					<Text>{formatMessage({id: 'baseline_templates.phase.days'}, {days: phase.durationDays})}</Text>
				) : null}
				<SubHeading>{formatHoursValue(intl, phaseHours(phase))}</SubHeading>
			</NestedCard.Header>
			<NestedCard.UncollapsibleContentWrapper>
				<NestedCard.Content>
					<Body gap="l">
						<FlexRow gap="m" alignItems="flex-start">
							<div data-field-path={`${phase.key}.name`} style={{flex: 2}}>
								<TextField
									label={formatMessage({id: 'baseline_templates.phase.name'})}
									data-cy={cy('name')}
									placeholder={formatMessage({id: 'baseline_templates.phase.name_placeholder'})}
									value={phase.name}
									onValueChange={(value: string) => onChange({name: value}, `${phase.key}.name`)}
									hasError={!!fieldError(`${phase.key}.name`)}
									errorText={fieldError(`${phase.key}.name`)}
								/>
							</div>
							<div data-field-path={`${phase.key}.startWeek`} style={{flex: 1}}>
								<NumberField
									label={formatMessage({id: 'baseline_templates.phase.start_week'})}
									data-cy={cy('start-week')}
									value={phase.startWeek ?? Number.NaN}
									onChange={(value: number) =>
										onChange({startWeek: toNullableNumber(value)}, `${phase.key}.startWeek`)
									}
									allowEmpty
									minValue={1}
									maximumFractionDigits={0}
									hasError={!!fieldError(`${phase.key}.startWeek`)}
									errorText={fieldError(`${phase.key}.startWeek`)}
								/>
							</div>
							<div data-field-path={`${phase.key}.durationDays`} style={{flex: 1}}>
								<NumberField
									label={formatMessage({id: 'baseline_templates.phase.duration_days'})}
									data-cy={cy('duration')}
									value={phase.durationDays ?? Number.NaN}
									onChange={(value: number) =>
										onChange({durationDays: toNullableNumber(value)}, `${phase.key}.durationDays`)
									}
									allowEmpty
									minValue={1}
									maximumFractionDigits={0}
									hasError={!!fieldError(`${phase.key}.durationDays`)}
									errorText={fieldError(`${phase.key}.durationDays`)}
								/>
							</div>
						</FlexRow>

						{includeDeliverables ? (
							<FlexRow gap="m" alignItems="flex-start">
								<div data-field-path={`${phase.key}.deliverableName`} style={{flex: 1}}>
									<TextField
										label={formatMessage({id: 'baseline_templates.phase.deliverable_name'})}
										data-cy={cy('deliverable-name')}
										placeholder={formatMessage({id: 'baseline_templates.phase.deliverable_placeholder'})}
										value={phase.deliverableName}
										onValueChange={(value: string) =>
											onChange({deliverableName: value}, `${phase.key}.deliverableName`)
										}
										hasError={!!fieldError(`${phase.key}.deliverableName`)}
										errorText={fieldError(`${phase.key}.deliverableName`)}
									/>
								</div>
								<div style={{flex: 1}}>
									<TextField
										label={formatMessage({id: 'baseline_templates.phase.deliverable_description'})}
										data-cy={cy('deliverable-description')}
										placeholder={formatMessage({id: 'baseline_templates.phase.deliverable_placeholder'})}
										value={phase.deliverableDescription}
										maxLength={DELIVERABLE_DESCRIPTION_MAX_LENGTH}
										onValueChange={(value: string) => onChange({deliverableDescription: value})}
									/>
								</div>
							</FlexRow>
						) : null}

						<Separator />
						<FlexColumn gap="s">
							<FlexRow gap="s" alignItems="center">
								<Icon icon="deliverablesTimeEstimate" size="l" />
								<SubHeading size="2">
									{formatMessage(
										{id: 'baseline_templates.phase.time_estimates'},
										{count: timeEstimateCount(phase)}
									)}
								</SubHeading>
							</FlexRow>
							<TimeEstimateLines
								phaseIndex={index}
								rows={phase.timeEstimates}
								roles={roles}
								heldRoles={heldRoles}
								fieldError={fieldError}
								onChange={(timeEstimates, changedPath) => onChange({timeEstimates}, changedPath)}
							/>
						</FlexColumn>

						<Separator />
						<FlexColumn gap="s">
							<FlexRow gap="s" alignItems="center">
								<Icon icon="expensesNew" size="l" />
								<SubHeading size="2">
									{formatMessage(
										{id: 'baseline_templates.phase.expense_estimates'},
										{count: expenseEstimateCount(phase)}
									)}
								</SubHeading>
							</FlexRow>
							<ExpenseEstimateLines
								phaseIndex={index}
								rows={phase.expenseEstimates}
								categories={categories}
								heldCategories={heldCategories}
								currency={currency}
								fieldError={fieldError}
								onChange={(expenseEstimates, changedPath) => onChange({expenseEstimates}, changedPath)}
							/>
						</FlexColumn>
					</Body>
				</NestedCard.Content>
			</NestedCard.UncollapsibleContentWrapper>
		</NestedCard>
	);
};
